import { withLocalStorage, withStorageSync } from '@ngrx-toolkit/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  effect,
  inject,
  InjectionToken,
  PLATFORM_ID,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';

export const AFTER_LOGIN_REDIRECT = new InjectionToken<() => boolean>(
  'AFTER_LOGIN_REDIRECT',
);
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStoreFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthNavigationService } from '@seatix/shared/auth/utils';
import { injectIsFanPortal } from '@seatix/shared/core';
import { catchError, EMPTY, finalize, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import { withUserResource } from './with-user-resource';
import {
  LoginCredentials,
  RegisterCredentials,
} from '@seatix/shared-domain-auth';

export function withAuthentication() {
  return signalStoreFeature(
    withState<{ navigate: boolean }>({
      navigate: false,
    }),
    withUserResource(),
    withCallStatus({
      prop: 'login',
      errorType: 'string',
    }),
    withCallStatus({
      prop: 'register',
      errorType: 'string',
    }),
    withCallStatus({
      prop: 'socialLogin',
      errorType: 'string',
    }),
    withProps(() => ({
      _authApiService: inject(AuthApiService),
      _authNavigationService: inject(AuthNavigationService),
      _isFanPortal: injectIsFanPortal(),
      _isBrowser: isPlatformBrowser(inject(PLATFORM_ID)),
      _router: inject(Router),
      _afterLoginRedirect: inject(AFTER_LOGIN_REDIRECT, { optional: true }),
    })),
    withMethods((store) => {
      const isFanPortal = store._isFanPortal;
      const authNavigationService = store._authNavigationService;
      const router = store._router;

      function resetCallStatus() {
        patchState(store, {
          loginCallStatus: 'init',
          registerCallStatus: 'init',
          socialLoginCallStatus: 'init',
        });
      }
      function clearAuth() {
        store.clearUser();
        patchState(store, { current_team: null });
        resetCallStatus();
      }
      function logoutLocal() {
        clearAuth();
        authNavigationService.navigateAfterSessionExpired();
      }
      function shouldNavigate() {
        patchState(store, { navigate: true });
      }
      function resetNavigationFlag() {
        patchState(store, { navigate: false });
      }
      function navigateAfterLogin() {
        if (!isFanPortal) {
          if (store._afterLoginRedirect?.()) return;
          const returnUrl = authNavigationService.getCurrentReturnUrl();
          authNavigationService.setReturnUrl(null);
          router.navigateByUrl(returnUrl ?? '/', { replaceUrl: true });
        }
      }

      return {
        resetCallStatus,
        logoutLocal,
        clearAuth,
        shouldNavigate,
        resetNavigationFlag,
        navigateAfterLogin,
      };
    }),
    withMethods((store) => {
      const authApiService = store._authApiService;
      const isFanPortal = store._isFanPortal;

      return {
        login: rxMethod<{
          credentials: LoginCredentials;
          turnstileToken: string;
        }>(
          pipe(
            switchMap(({ credentials, turnstileToken }) => {
              store.resetCallStatus();
              store.setLoginLoading();
              store.shouldNavigate();

              return authApiService.login(credentials, turnstileToken).pipe(
                tap((response) => {
                  store.setUser(response.data);
                  store.setLoginLoaded();
                }),
                catchError((error) => {
                  store.setLoginError(error.userMessage);
                  store.resetNavigationFlag();
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        register: rxMethod<{
          credentials: RegisterCredentials;
          turnstileToken: string;
        }>(
          pipe(
            switchMap(({ credentials, turnstileToken }) => {
              store.resetCallStatus();
              store.setRegisterLoading();
              store.shouldNavigate();

              return authApiService.register(credentials, turnstileToken).pipe(
                tap((response) => {
                  store.setUser(response.data);
                  store.setRegisterLoaded();
                }),
                catchError((error) => {
                  store.setRegisterError(error.userMessage);
                  store.resetNavigationFlag();
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        socialLogin: rxMethod<string>(
          pipe(
            switchMap((token) => {
              store.resetCallStatus();
              store.setSocialLoginLoading();
              store.shouldNavigate();

              return authApiService
                .handleGoogleCallback(token, !isFanPortal)
                .pipe(
                  tap((response) => {
                    store.setUser(response.data);
                    store.setSocialLoginLoaded();
                  }),
                  catchError((error) => {
                    store.setSocialLoginError(
                      error.userMessage || 'Social login failed',
                    );
                    store.resetNavigationFlag();
                    return EMPTY;
                  }),
                );
            }),
          ),
        ),

        logout: rxMethod<void>(
          pipe(
            switchMap(() => {
              return authApiService.logout().pipe(
                catchError(() => {
                  return EMPTY;
                }),
                finalize(() => {
                  store.clearAuth();
                  store._authNavigationService.navigateAfterLogout();
                }),
              );
            }),
          ),
        ),
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        effect(() => {
          const user = store.user();
          const wasLoading =
            store.isLoginLoading() ||
            store.isRegisterLoading() ||
            store.isSocialLoginLoading();

          if (user && !wasLoading && store.navigate()) {
            store.navigateAfterLogin();
            store.resetNavigationFlag();
          }
        });

        effect(() => {
          // Guard with hasValue(): reading value() while the resource is in an
          // error state throws. fan-portal's revalidation now lets /me settle in
          // error (it's EXPECTED_UNAUTHORIZED, so the interceptor no longer
          // destroys the resource first), which would surface that throw here.
          if (store.userResource.hasValue()) {
            // hasValue() is `value() !== undefined`, so a null payload would
            // still reach setUser() and throw on user.teams.
            const value = store.userResource.value();
            if (value) {
              store.setUser(value);
            }
          }
        });

        // When localStorage has no auth (e.g. after a cross-app redirect when
        // it's not fan-portal), activate userResource to silently re-authenticate
        // via session cookie.
        effect(() => {
          if (!store.user() && !store._isFanPortal) {
            store.setResourceActive(true);
          }
        });

        // fan-portal trusts localStorage indefinitely; re-validate a stored
        // session against the server. Client-only (localStorage is browser-only)
        // so it never adds server-render latency. setUser on a 200 flips
        // sessionValidated, so this fires /me once and then settles.
        effect(() => {
          if (
            store._isFanPortal &&
            store._isBrowser &&
            store.user() &&
            !store.sessionValidated()
          ) {
            store.setResourceActive(true);
          }
        });

        // fan-portal marks /me EXPECTED_UNAUTHORIZED, so its 401 skips the
        // interceptor's logoutLocal() (which would navigate to /events). A 401
        // means the stored session is dead → clear it silently. A transient
        // error (network down, 5xx) must NOT log out a user with a valid
        // session, so only a 401 clears.
        effect(() => {
          if (!store._isFanPortal) {
            return;
          }
          const error = store.userResource.error();
          if (
            error instanceof HttpErrorResponse &&
            error.status === HttpStatusCode.Unauthorized
          ) {
            untracked(() => store.clearAuth());
          }
        });
      },
    })),
    withStorageSync(
      {
        key: 'auth',
        select: (store) => ({
          user: store.user,
          current_team: store.current_team,
        }),
        // The library's default parse is an unguarded JSON.parse. A corrupt
        // localStorage['auth'] must boot the app logged-out instead of throwing
        // during store init. Re-parsing an empty slice keeps the return the
        // shape the config expects.
        parse: (value) => {
          try {
            return JSON.parse(value);
          } catch {
            return { user: null, current_team: null };
          }
        },
      },
      withLocalStorage(),
    ),
  );
}
